import { MAX_CHAT_MESSAGES_PER_CHANNEL } from '@game/constants';
import { PartyMembership } from '@game/enums';
import type { WorkoutRecord } from '@game/game-data';
import type { ClientChatMessage, ClientCooldown, ClientEffect, ClientNotificationRecord, ClientParty } from '@game/shared-types';
import { create } from 'zustand';

import { getPublicAvatarURL } from '@util/getPublicAvatarURL';

import { MappedEquipment } from '../views/Equipment/MappedEquipment';
import type { ChatState } from './chatState';
import { chatDefault } from './chatState';
import { missionDefault, type MissionState } from './missionState';
import type { PartyState } from './partyState';
import { partyDefault } from './partyState';
import { persistentStore } from './persistentStore';
import type { PlayerState } from './playerState';
import { playerDefault } from './playerState';
import type { SettingsState } from './settingsState';
import { settingsDefault } from './settingsState';
import type { UIState } from './uiState';
import { uiDefault } from './uiState';
import type { UserState } from './userState';
import { userDefault } from './userState';

interface Store extends PlayerState, UserState, SettingsState, UIState, ChatState, PartyState, MissionState {
  resetState: () => void;
  removeEffectsItem: (effectsItem: ClientEffect) => void;
  removeCooldownsItem: (cooldownsItem: ClientCooldown) => void;
  addNotification: (notification: ClientNotificationRecord) => void;
  updateResourceTicks: (energyNextTick: number, spiritNextTick: number) => void;
  addChatMessage: (chatMessage: ClientChatMessage) => void;
  removeChatMessage: (channel: string, messageId: string) => void;
  prependChatMessage: (chatMessage: ClientChatMessage) => void;
  updateWorkoutStat: (workoutStats: WorkoutRecord) => void;
  unequipItem: (itemName: string) => void;
  setChatAvatar: (name: string, avatarUrl: string) => void;
  incrementUnreadChatMessageCount: (channel: string) => void;
  setParty: (party: ClientParty) => void;
  setOpenedUrl: (url: URL | undefined) => void;
  addChatChannel: (chatChannel: { name: string; alias: string }) => void;
  setChatChannel: (chatChannel: string) => void;
  reduceChatChannelFirstItemIndex: (chatChannel: string, amount: number) => void;
  setChatChannelHasOlderMessages: (chatChannel: string, hasOlderMessages: boolean) => void;
}

export const store = create<Store>((set, get) => ({
  ...userDefault,
  ...playerDefault,
  ...settingsDefault,
  ...uiDefault,
  ...chatDefault,
  ...partyDefault,
  ...missionDefault,
  unequipItem: (itemName: string): void => {
    const filteredEquipment = get().equipment.filter((equipment) => equipment.item.name !== itemName);
    set((state) => ({
      ...state,
      equipment: filteredEquipment,
      mappedEquipment: MappedEquipment({ equipment: filteredEquipment }),
    }));
  },
  resetState: (): void => {
    set((state) => ({ ...state, ...userDefault, ...playerDefault, ...uiDefault, ...chatDefault }));
  },
  removeEffectsItem: (effectsItem: ClientEffect): void => {
    set((state) => ({
      ...state,
      effects: state.effects.filter((effect: ClientEffect) => effect.id !== effectsItem.id),
    }));
  },
  removeCooldownsItem: (cooldownsItem: ClientCooldown): void => {
    set((state) => ({
      ...state,
      cooldowns: state.cooldowns.filter((cooldown: ClientCooldown) => cooldown.id !== cooldownsItem.id),
    }));
  },
  addNotification: (notification: ClientNotificationRecord): void => {
    set((state) => ({
      ...state,
      notifications: [...state.notifications, notification],
    }));
  },
  updateResourceTicks: (energyNextTick, spiritNextTick): void => {
    if (energyNextTick === store.getState().energyNextTick && spiritNextTick === store.getState().spiritNextTick) {
      return;
    }
    set((state) => ({
      ...state,
      energyNextTick,
      spiritNextTick,
    }));
  },
  setChatChannel: (chatChannelName: string): void => {
    set((state) => {
      return {
        ...state,
        chatChannelMessages: state.chatMessages.filter((message) => message.channelName === chatChannelName),
        channelsUnreadMessages: { ...state.channelsUnreadMessages, [chatChannelName]: 0 },
      };
    });
  },
  addChatChannel: (chatChannel: { name: string; alias: string }): void => {
    set((state) => ({
      ...state,
      chatChannels: { ...state.chatChannels, [chatChannel.name]: chatChannel.alias },
    }));
  },
  reduceChatChannelFirstItemIndex: (chatChannel: string, amount: number): void => {
    set((state) => {
      const currentIndex = state.channelFirstItemIndexes[chatChannel];

      return {
        ...state,
        channelFirstItemIndexes: {
          ...state.channelFirstItemIndexes,
          [chatChannel]: currentIndex === undefined ? MAX_CHAT_MESSAGES_PER_CHANNEL - amount : Math.max(0, currentIndex - amount),
        },
      };
    });
  },
  setChatChannelHasOlderMessages: (chatChannel: string, hasOlderMessages: boolean): void => {
    set((state) => ({
      ...state,
      channelHasOlderMessages: { ...state.channelHasOlderMessages, [chatChannel]: hasOlderMessages },
    }));
  },
  addChatMessage: (chatMessage): void => {
    const avatarUrl = chatMessage.user.avatar ? getPublicAvatarURL(chatMessage.user.avatar) : '';
    const storedAvatar = store.getState().chatAvatars[chatMessage.user.name];
    const chatChannel = persistentStore.getState().chatChannel;

    // NOTE: Only update the avatar if it has changed
    if (avatarUrl !== storedAvatar) {
      // Add the avatar to the chatAvatars map, which is a cache of all the avatars
      store.getState().setChatAvatar(chatMessage.user.name, avatarUrl);
    }

    // NOTE: If the chat channel is not the same as the message channel, increment the unread chat message count
    if (chatChannel !== chatMessage.channelName) {
      // If the timestamp on the chat message is newer than the last time the channel was read, increment the unread chat message count
      const lastReadTime = persistentStore.getState().chatChannelsLastReadTime[chatMessage.channelName];
      if (!lastReadTime || lastReadTime < chatMessage.createdAt.getTime()) {
        store.getState().incrementUnreadChatMessageCount(chatMessage.channelName);
      }
    }

    const newChatMessage = {
      ...chatMessage,
      user: {
        ...chatMessage.user,
        avatar: '',
      },
    };

    if (!persistentStore.getState().showChatPanel) {
      store.setState({ unreadChatMessageCount: store.getState().unreadChatMessageCount + 1 });
    }
    const currentChatChannel = persistentStore.getState().chatChannel;
    set((state) => ({
      ...state,
      chatChannelMessages:
        chatMessage.channelName === currentChatChannel ? [...state.chatChannelMessages, newChatMessage] : state.chatChannelMessages,
      chatMessages: [...state.chatMessages, newChatMessage],
    }));
  },
  removeChatMessage: (channel, messageId): void => {
    const currentChatChannel = persistentStore.getState().chatChannel;
    set((state) => ({
      ...state,
      chatChannelMessages:
        channel === currentChatChannel ? state.chatChannelMessages.filter((message) => message.messageId !== messageId) : state.chatChannelMessages,
      chatMessages: state.chatMessages.filter((message) => message.messageId !== messageId),
    }));
  },
  prependChatMessage: (chatMessage): void => {
    const currentChatChannel = persistentStore.getState().chatChannel;
    set((state) => ({
      ...state,
      chatChannelMessages: chatMessage.channelName === currentChatChannel ? [chatMessage, ...state.chatChannelMessages] : state.chatChannelMessages,
      chatMessages: [chatMessage, ...state.chatMessages],
    }));
  },
  updateWorkoutStat: (workoutStats): void => {
    set((state) => ({
      ...state,
      workoutStats: { ...state.workoutStats, ...workoutStats },
    }));
  },
  setChatAvatar: (name: string, avatarUrl: string): void => {
    set((state) => ({
      ...state,
      chatAvatars: { ...state.chatAvatars, [name]: avatarUrl },
    }));
  },
  incrementUnreadChatMessageCount: (channel: string): void => {
    set((state) => ({
      ...state,
      channelsUnreadMessages: { ...state.channelsUnreadMessages, [channel]: (state.channelsUnreadMessages[channel] ?? 0) + 1 },
    }));
  },
  setParty: (party: ClientParty): void => {
    const isPartyLeader = party.leaderName === get().name;
    let isPartyInvitee = false;
    let isPartyMember = false;

    for (const member of party.members) {
      if (member.id === get().id) {
        isPartyInvitee = member.membership === PartyMembership.Invited;
        isPartyMember = member.membership === PartyMembership.Member;
        break;
      }
    }
    set((state) => ({
      ...state,
      party,
      isPartyLeader,
      isPartyMember,
      isPartyInvitee,
    }));
  },
  setOpenedUrl: (url: URL | undefined): void => {
    set((state) => ({
      ...state,
      openedUrl: url,
    }));
  },
}));
